import React, { memo, useEffect, useState, HTMLInputTypeAttribute, HTMLAttributes } from "react";
import { EyeFilled, EyeInvisibleFilled } from "@ant-design/icons";
import "./Input.less";

interface Props extends HTMLAttributes<HTMLInputElement> {
   autocomplete?: string;
   allowClear?: boolean;
   autoFocus?: boolean;
   className?: string;
   customClassName?: string;
   name?: string;
   disabled?: boolean;
   id?: string;
   defaultValue?: string;
   value?: string;
   size?: "large" | "default" | "small";
   style?: React.CSSProperties;
   type?: HTMLInputTypeAttribute;
   isLoading?: boolean | undefined;
   placeholder?: string;
   required?: boolean;
   ref?: HTMLInputElement;
   onChange?: (e: React.ChangeEvent<HTMLInputElement>, blur?: boolean) => void;
   onPressEnter?: (e: KeyboardEvent) => void;
   onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
   min?: number;
   max?: number;
   precision?: number;
   step?: number;
   onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
   onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
   onCut?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
   refreshOnValueChange?: boolean;
   emitOnlyWhenFocused?: boolean;
}

export const Input = memo(
   React.forwardRef<HTMLInputElement, Props>(
      (
         {
            autocomplete,
            allowClear = false,
            autoFocus = false,
            className,
            customClassName,
            name,
            disabled = false,
            id,
            defaultValue,
            value,
            size = "default",
            type = "text",
            style,
            placeholder = "",
            required = false,
            isLoading = false,
            onChange,
            onBlur,
            onPressEnter,
            onKeyDown,
            onPaste,
            onCut,
            refreshOnValueChange = false,
            min,
            max,
            precision,
            step = 1,
            emitOnlyWhenFocused = true,
         },
         ref,
      ) => {
         const [textValue, setTextValue] = useState<string>(defaultValue ?? "");
         const [isFocused, setIsFocused] = useState<boolean>(false);
         const inputRef = React.useRef<HTMLInputElement>(null);
         const [showPassword, setShowPassword] = useState<boolean>(false);
         const formatInitialValue = type === "number" && !isFocused && value;
         const nullChangeEvent: React.ChangeEvent<HTMLInputElement> = {
            // @ts-ignore
            target: {
               value: "",
            },
         };

         useEffect(() => {
            if (refreshOnValueChange && value !== undefined) {
               setTextValue(value);
            }
         }, [refreshOnValueChange, value, defaultValue]);

         const nullKeyboardEvent = new KeyboardEvent("keydown", {
            key: "Enter",
            code: "Enter",
            keyCode: 13,
            // @ts-ignore
            target: {
               value: "",
            },
         });

         const formatValue = (value: string) => {
            return Number(value).toFixed(precision);
         };
         const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
            if (!value) {
               setTextValue(e.target.value);
            }

            if (typeof onChange === "function" && (!emitOnlyWhenFocused || isFocused)) onChange(e);
         };

         const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (type === "number" && precision !== undefined && value && !isNaN(Number(value))) {
               const formattedValue = formatValue(value ?? textValue);
               onChange && onChange({ ...e, target: { ...e.target, value: formattedValue } }, true);
            }
            setIsFocused(false);
            onBlur && onBlur(e);
         };

         const togglePasswordVisibility = () => {
            setShowPassword(!showPassword);
         };

         useEffect(() => {
            const handleEnter = (e: KeyboardEvent) => {
               if (
                  e.key === "Enter" &&
                  typeof onPressEnter === "function" &&
                  inputRef.current === e.target
               )
                  onPressEnter(e);
            };

            if (typeof onPressEnter === "function")
               document.addEventListener("keydown", handleEnter);

            return () => {
               if (typeof onPressEnter === "function")
                  document.removeEventListener("keydown", handleEnter);
            };
         }, [onPressEnter]);

         const clearValue = () => {
            setTextValue("");
            onChange && onChange(nullChangeEvent);
            onPressEnter && onPressEnter(nullKeyboardEvent);
         };
         return (
            <div className={`bp2input ${className ? className : ""}`}>
               {id === "de-input" || id === "en-input" || id === "pl-input" || id === "sp-input" ? (
                  <span className="language-prefix p3">{id?.slice(0, 2)}</span>
               ) : null}
               <input
                  id={id}
                  data-testid="input"
                  className={customClassName}
                  value={
                     formatInitialValue ? formatValue(value ?? textValue) : (value ?? textValue)
                  }
                  data-size={size}
                  name={name}
                  onChange={handleChange}
                  type={type === "password" ? (showPassword ? "text" : "password") : type}
                  {...(type === "number" && min !== undefined ? { min } : {})}
                  {...(type === "number" && max !== undefined ? { max } : {})}
                  {...(type === "number" && step !== undefined ? { step } : {})}
                  disabled={disabled}
                  style={style}
                  placeholder={placeholder}
                  required={required}
                  onBlur={handleBlur}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                  onCut={onCut}
                  onFocus={() => setIsFocused(true)}
                  ref={ref ? ref : inputRef}
                  autoFocus={autoFocus}
                  autoComplete={autocomplete}
               />
               {!!textValue?.length && allowClear && (
                  <button data-testid="clear-button" type="button" onClick={clearValue}>
                     <svg
                        viewBox="0 0 24 24"
                        width="12"
                        height="12"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                     </svg>
                  </button>
               )}
               {type === "password" && (
                  <button
                     onClick={togglePasswordVisibility}
                     type="button"
                     className="password-visibility-btn"
                  >
                     {showPassword ? <EyeFilled /> : <EyeInvisibleFilled />}
                  </button>
               )}
               {/* {isLoading ? <Spin style={{position: "absolute", display: "inline-block"}}></Spin> : null} */}
            </div>
         );
      },
   ),
);
